import { stagesSum, embryosCreated } from '../../../core/constants/clivage';

// Clivage grid validation. embryos_count = Σ stages (the v1 model). Per item:
// donor, semen, oocytes(int≥0) required; embryos (=Σ stages) ≤ oocytes;
// preservation required when there are embryos. Conservation: Σ oocytes per
// aspiration ≤ available (backend enforces the final 422 under a lock).

const isInt = (v) => v !== '' && Number.isInteger(Number(v)) && Number(v) >= 0;

// A seeded donor row the user never filled (no oocytes, no stages) is ignored on
// save — so pre-seeding one row per donor never forces clivating every donor.
export function isEmptyRow(r) {
    return r.oocytes === '' && stagesSum(r.stages) === 0;
}

// ctx: { sameSemen, globalSemen, lab, availableByAsp }
export function validateClivages(rows, ctx) {
    const effSemen = (r) => (ctx.sameSemen ? ctx.globalSemen : r.semen);
    // Una donadora sembrada que el usuario NO fertilizó (sin semen efectivo ni
    // embriones) se ignora → no obliga a clivar todas las donadoras; el semen es la
    // señal de "fertilizar esta vaca" (las filas con embriones pero sin semen sí se
    // consideran para que salte el error 'Elegí el semen').
    const considered = rows.filter(r => !isEmptyRow(r) && (effSemen(r) || stagesSum(r.stages) > 0));
    const rowErrors = {};
    const setErr = (key, field, msg) => { (rowErrors[key] ||= {})[field] = msg; };

    considered.forEach((r) => {
        if (!r.aspiration_id) setErr(r.key, 'donor', 'Elegí la donante.');
        if (!isInt(r.oocytes)) setErr(r.key, 'oocytes', 'Óvocitos inválidos.');

        const semen = ctx.sameSemen ? ctx.globalSemen : r.semen;
        if (!semen) setErr(r.key, 'semen', 'Elegí el semen.');

        // óvocitos de una fila no puede superar los DISPONIBLES de la donante
        // (oocytes_disponibles ≤ viables; coherente con el medidor y la conservación).
        const cap = r.available != null ? r.available : r.viables;
        if (isInt(r.oocytes) && cap != null && Number(r.oocytes) > cap) {
            setErr(r.key, 'oocytes', `Óvocitos > disponibles (${cap}).`);
        }

        const embryos = stagesSum(r.stages);
        // embriones no puede superar los óvocitos usados (cubre óv=0 con emb>0).
        if (isInt(r.oocytes) && embryos > Number(r.oocytes)) {
            setErr(r.key, 'embryos', 'Embriones > óvocitos.');
        }
        // preservación obligatoria solo si hay ≥1 embrión no-DG (DG no se preserva, 0025 §2).
        if (embryosCreated(r.stages) > 0 && !r.preservation) setErr(r.key, 'preservation', 'Elegí preservación.');
    });

    // conservation: Σ oocytes per aspiration ≤ available
    const byAsp = {};
    considered.forEach((r) => {
        if (!r.aspiration_id || !isInt(r.oocytes)) return;
        (byAsp[r.aspiration_id] ||= []).push(r);
    });
    Object.entries(byAsp).forEach(([aspId, group]) => {
        const sum = group.reduce((a, r) => a + Number(r.oocytes), 0);
        const avail = ctx.availableByAsp?.[aspId];
        if (avail != null && sum > avail) {
            group.forEach(r => setErr(r.key, 'oocytes', `Supera disponibles (${avail}).`));
        }
    });

    let formError = null;
    if (!ctx.lab) formError = 'Seleccioná el laboratorio.';
    else if (ctx.sameSemen && !ctx.globalSemen) formError = 'Seleccioná el semen común.';

    const valid = considered.length > 0 && Object.keys(rowErrors).length === 0 && !formError;
    return { rowErrors, valid, considered, formError };
}
